<template>
  <header class="ls-masthead">
    <div class="ls-masthead__inner">
      <a
        class="ls-masthead__brand"
        href="#ls-main"
        :aria-label="`${brandName}, back to the top`"
        @click.prevent="toTop"
      >
        <img
          v-if="logo && logo.url && !logoFailed"
          class="ls-masthead__logo"
          :src="logo.url"
          :alt="brandName"
          :width="logo.w || null"
          :height="logo.h || null"
          decoding="async"
          @error="logoFailed = true"
        >
        <span
          v-else
          class="ls-masthead__wordmark"
          data-test="ls-wordmark"
        >{{ brandName }}</span>
      </a>
      <span
        v-if="sheetName"
        class="ls-masthead__sheet"
      >{{ sheetName }}</span>
      <button
        type="button"
        class="ls-masthead__pill"
        data-test="ls-shortlist-pill"
        :aria-label="`Shortlist, ${shortlistCount} ${shortlistCount === 1 ? 'style' : 'styles'}`"
        @click="$emit('shortlist')"
      >
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          aria-hidden="true"
          focusable="false"
        ><path
          d="M12 20.5s-7.5-4.6-7.5-10.1A4.4 4.4 0 0 1 12 7.6a4.4 4.4 0 0 1 7.5 2.8c0 5.5-7.5 10.1-7.5 10.1Z"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linejoin="round"
        /></svg>
        <span>Shortlist</span>
        <span class="ls-masthead__count">{{ shortlistCount }}</span>
      </button>
      <div
        v-if="$slots.chip"
        class="ls-masthead__chip"
      >
        <slot name="chip" />
      </div>
    </div>
  </header>
</template>

<script>
export default {
  name: 'LsMasthead',
  props: {
    logo: { type: Object, default: null },
    brandName: { type: String, required: true },
    sheetName: { type: String, default: '' },
    shortlistCount: { type: Number, default: 0 },
  },
  data() {
    return { logoFailed: false }
  },
  methods: {
    toTop() {
      window.scrollTo({ top: 0 })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './ls-base';

.ls-masthead {
  position: sticky;
  top: 0;
  z-index: 30;
  background: var(--ls-ground);
  border-bottom: 1px solid var(--ls-line);

  &__inner {
    display: flex;
    align-items: center;
    gap: 16px;
    height: 56px;
    max-width: $ls-max;
    margin: 0 auto;
    padding: 0 $ls-gutter;
  }

  &__brand {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    margin-right: auto;
    color: var(--ls-ink);
    text-decoration: none;
    @include ls-focus;
  }

  &__logo {
    display: block;
    width: auto;
    height: auto;
    max-height: 28px;
    max-width: 160px;
    object-fit: contain;
  }

  &__wordmark {
    @include ls-heading;
    font-size: 1.25rem;
    color: var(--ls-ink);
  }

  &__sheet {
    display: none;
    @include ls-label;
    color: var(--ls-muted);
    font-weight: 500;
    max-width: 36ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @include ls-up($ls-md) {
      display: block;
    }
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    padding: 0 16px;
    border: 1px solid var(--ls-line);
    border-radius: 999px;
    background: transparent;
    color: var(--ls-ink);
    font: 500 0.875rem/1 var(--ls-body-font);
    cursor: pointer;
    touch-action: manipulation;
    transition: border-color 0.2s $ls-ease;
    @include ls-focus;

    &:hover {
      border-color: var(--ls-ink);
    }
  }

  // KP-4572: who the buyer comments as; on phones it sits in the order bar.
  &__chip {
    display: none;
    min-width: 0;
    max-width: 32ch;

    @include ls-up($ls-md) {
      display: flex;
    }
  }

  &__count {
    min-width: 1.5em;
    padding: 3px 6px;
    border-radius: 999px;
    background: var(--ls-primary);
    color: var(--ls-on-primary);
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
    text-align: center;
  }
}
</style>
